import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  getAgendamentosAdmin,
  getProfissionais,
  getUnidades,
  getServicos,
  searchUsuariosAdmin,
  createAgendamentoAdmin,
  updateAgendamentoAdmin,
  updateAgendamentoStatusAdmin,
  type AdminAgendamentoItem,
  type AdminAgendamentoStatus,
  type Profissional,
  type Unidade,
  type Servico,
  type UsuarioSearchRow,
} from '@/api/adminApi'
import { useSaveFeedback } from '@/hooks/useSaveFeedback'
import styles from './Admin.module.css'

const PAGE_SIZE = 25

function formatDataPt(dateStr: string) {
  if (!dateStr) return '—'
  const [y, m, d] = String(dateStr).slice(0, 10).split('-')
  if (!y || !m || !d) return dateStr
  const meses = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez']
  return `${Number(d)} ${meses[Number(m) - 1] ?? m} ${y}`
}

function statusLabel(status: string) {
  if (status === 'realizado') return 'Realizado'
  if (status === 'cancelado') return 'Cancelado'
  if (status === 'agendado') return 'Agendado'
  return status || '—'
}

function statusClass(status: string) {
  if (status === 'realizado') return styles.agStatusOk
  if (status === 'cancelado') return styles.agStatusOff
  return styles.agStatusPending
}

/** Nome gerado automaticamente a partir do e-mail no checkout visitante (ex.: tamiettojunior@… → Tamiettojunior). */
function isNomeDerivadoDoEmail(nome: string, email: string): boolean {
  const local = email.split('@')[0] || ''
  const nomeNorm = nome.replace(/\s+/g, '').toLowerCase()
  const localNorm = local.replace(/[._-]+/g, '').toLowerCase()
  const localAsWords = local
    .replace(/[._-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim()
  return (
    nomeNorm === localNorm ||
    nome.trim().toLowerCase() === local.toLowerCase() ||
    nome.trim() === localAsWords
  )
}

/**
 * Identificador na lista: prefere e-mail/telefone (dados que o guest preenche)
 * em vez do nome automático derivado do e-mail.
 */
function resolveClienteIdentificador(r: AdminAgendamentoItem): { primary: string; secondary: string } {
  const email = r.cliente_email?.trim() || ''
  const tel = r.cliente_telefone?.trim() || ''
  const nome = r.cliente_nome?.trim() || ''
  const nomeUtil = nome && !(email && isNomeDerivadoDoEmail(nome, email)) ? nome : ''

  const primary = email || tel || nomeUtil || nome || `Cliente #${r.usuario_id}`
  const parts: string[] = []
  if (nomeUtil && primary !== nomeUtil) parts.push(nomeUtil)
  if (email && primary !== email) parts.push(email)
  if (tel && primary !== tel) parts.push(tel)
  if (parts.length === 0) parts.push(`ID ${r.usuario_id}`)
  return { primary, secondary: parts.join(' · ') }
}

function escapeCsv(val: string | number | null | undefined): string {
  const s = val == null ? '' : String(val)
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`
  return s
}

function downloadCsv(items: AdminAgendamentoItem[], showServico: boolean) {
  const headers = [
    'ID',
    'Data',
    'Hora',
    'Cliente',
    'Email',
    'Telefone',
    'Profissional',
    'Unidade',
    ...(showServico ? ['Serviço'] : []),
    'Estado',
  ]
  const rows = items.map((r) => {
    const id = resolveClienteIdentificador(r)
    return [
      r.id,
      r.data,
      r.hora,
      id.primary,
      r.cliente_email ?? '',
      r.cliente_telefone ?? '',
      r.profissional,
      r.unidade,
      ...(showServico ? [r.servico_nome ?? ''] : []),
      r.status,
    ]
      .map(escapeCsv)
      .join(',')
  })
  const csv = [headers.join(','), ...rows].join('\n')
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `agendamentos-${new Date().toISOString().slice(0, 10)}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

type EditState = {
  data: string
  hora: string
  profissional_id: string
  unidade_id: string
}

function statusSortRank(status: string): number {
  if (status === 'agendado') return 0
  if (status === 'realizado') return 1
  if (status === 'cancelado') return 2
  return 3
}

function sortAgendamentos(items: AdminAgendamentoItem[]): AdminAgendamentoItem[] {
  return [...items].sort((a, b) => {
    const byStatus = statusSortRank(a.status) - statusSortRank(b.status)
    if (byStatus !== 0) return byStatus
    const byDate = String(b.data).localeCompare(String(a.data))
    if (byDate !== 0) return byDate
    return String(b.hora).localeCompare(String(a.hora))
  })
}

export function AdminAgendamentosPage() {
  const [profissionais, setProfissionais] = useState<Profissional[]>([])
  const [unidades, setUnidades] = useState<Unidade[]>([])
  const [servicos, setServicos] = useState<Servico[]>([])
  const [rows, setRows] = useState<AdminAgendamentoItem[]>([])
  const [total, setTotal] = useState(0)
  const [pages, setPages] = useState(1)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [savingId, setSavingId] = useState<number | null>(null)
  const { notifySaved, notifyError, notifyValidation } = useSaveFeedback()

  const [filtroQ, setFiltroQ] = useState('')
  const [filtroProf, setFiltroProf] = useState('')
  const [filtroStatus, setFiltroStatus] = useState('')
  const [filtroDesde, setFiltroDesde] = useState('')
  const [filtroAte, setFiltroAte] = useState('')

  const [showNovo, setShowNovo] = useState(false)
  const [novoUsuarioQ, setNovoUsuarioQ] = useState('')
  const [novoUsuarios, setNovoUsuarios] = useState<UsuarioSearchRow[]>([])
  const [novoUsuarioId, setNovoUsuarioId] = useState<number | null>(null)
  const [novoProfId, setNovoProfId] = useState('')
  const [novoUnidadeId, setNovoUnidadeId] = useState('')
  const [novoServicoId, setNovoServicoId] = useState('')
  const [novoData, setNovoData] = useState('')
  const [novoHora, setNovoHora] = useState('')
  const [novoSaving, setNovoSaving] = useState(false)
  const [searchingUsers, setSearchingUsers] = useState(false)

  const [editingId, setEditingId] = useState<number | null>(null)
  const [editForm, setEditForm] = useState<EditState>({
    data: '',
    hora: '',
    profissional_id: '',
    unidade_id: '',
  })

  useEffect(() => {
    Promise.all([getProfissionais(true), getUnidades(true), getServicos(false)])
      .then(([p, u, s]) => {
        setProfissionais(p)
        setUnidades(u)
        setServicos(s)
        if (u.length > 0) setNovoUnidadeId(String(u[0].id))
      })
      .catch(() => {
        setProfissionais([])
        setUnidades([])
        setServicos([])
      })
  }, [])

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    const query: Parameters<typeof getAgendamentosAdmin>[0] = { page, limit: PAGE_SIZE }
    if (filtroQ.trim()) query.q = filtroQ.trim()
    if (filtroProf) query.profissional_id = Number(filtroProf)
    if (filtroStatus) query.status = filtroStatus as AdminAgendamentoStatus
    if (filtroDesde) query.data_desde = filtroDesde
    if (filtroAte) query.data_ate = filtroAte

    getAgendamentosAdmin(query)
      .then((res) => {
        setRows(res.items)
        setTotal(res.total)
        setPages(res.pages)
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Erro ao carregar'))
      .finally(() => setLoading(false))
  }, [page, filtroQ, filtroProf, filtroStatus, filtroDesde, filtroAte])

  useEffect(() => {
    load()
  }, [load])

  const showServicoCol = useMemo(() => rows.some((r) => r.servico_nome), [rows])

  const displayRows = useMemo(() => sortAgendamentos(rows), [rows])

  const applyFilters = () => {
    setPage(1)
    load()
  }

  const searchUsers = async () => {
    if (novoUsuarioQ.trim().length < 2) {
      notifyValidation('Digite pelo menos 2 caracteres para pesquisar.')
      return
    }
    setSearchingUsers(true)
    try {
      const found = await searchUsuariosAdmin(novoUsuarioQ)
      setNovoUsuarios(found)
      if (found.length === 1) setNovoUsuarioId(found[0].id)
    } catch (e) {
      notifyError(e, 'Erro na pesquisa')
    } finally {
      setSearchingUsers(false)
    }
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!novoUsuarioId) {
      notifyValidation('Seleccione um cliente.')
      return
    }
    if (!novoProfId || !novoUnidadeId || !novoData || !novoHora) {
      notifyValidation('Preencha profissional, unidade, data e hora.')
      return
    }
    setNovoSaving(true)
    setError(null)
    try {
      await createAgendamentoAdmin({
        usuario_id: novoUsuarioId,
        profissional_id: Number(novoProfId),
        unidade_id: Number(novoUnidadeId),
        data: novoData,
        hora: novoHora.slice(0, 5),
        servico_id: novoServicoId ? Number(novoServicoId) : null,
      })
      notifySaved('Agendamento criado.')
      setShowNovo(false)
      setNovoUsuarioQ('')
      setNovoUsuarios([])
      setNovoUsuarioId(null)
      setNovoData('')
      setNovoHora('')
      setNovoServicoId('')
      load()
    } catch (e) {
      notifyError(e, 'Erro ao criar')
      setError(e instanceof Error ? e.message : 'Erro ao criar')
    } finally {
      setNovoSaving(false)
    }
  }

  const startEdit = (r: AdminAgendamentoItem) => {
    setEditingId(r.id)
    setEditForm({
      data: String(r.data).slice(0, 10),
      hora: String(r.hora).slice(0, 5),
      profissional_id: r.profissional_id ? String(r.profissional_id) : '',
      unidade_id: r.unidade_id ? String(r.unidade_id) : '',
    })
  }

  const cancelEdit = () => {
    setEditingId(null)
  }

  const saveEdit = async (id: number) => {
    if (!editForm.data || !editForm.hora || !editForm.profissional_id || !editForm.unidade_id) {
      notifyValidation('Preencha data, hora, profissional e unidade.')
      return
    }
    setSavingId(id)
    setError(null)
    try {
      await updateAgendamentoAdmin(id, {
        data: editForm.data,
        hora: editForm.hora.slice(0, 5),
        profissional_id: Number(editForm.profissional_id),
        unidade_id: Number(editForm.unidade_id),
      })
      setEditingId(null)
      notifySaved('Agendamento reagendado.')
      load()
    } catch (e) {
      notifyError(e, 'Erro ao reagendar')
      setError(e instanceof Error ? e.message : 'Erro ao reagendar')
    } finally {
      setSavingId(null)
    }
  }

  const onStatus = async (id: number, status: AdminAgendamentoStatus) => {
    setSavingId(id)
    setError(null)
    try {
      await updateAgendamentoStatusAdmin(id, status)
      setRows((prev) => sortAgendamentos(prev.map((r) => (r.id === id ? { ...r, status } : r))))
      notifySaved('Estado do agendamento actualizado.')
    } catch (e) {
      notifyError(e, 'Erro ao actualizar')
      setError(e instanceof Error ? e.message : 'Erro ao actualizar')
    } finally {
      setSavingId(null)
    }
  }

  const renderRowActions = (r: AdminAgendamentoItem, isEditing: boolean) => (
    <div className={styles.agRowActions}>
      {isEditing ? (
        <>
          <button
            type="button"
            className={styles.btnSalvar}
            disabled={savingId === r.id}
            onClick={() => saveEdit(r.id)}
          >
            Guardar
          </button>
          <button type="button" className={styles.btnCancel} onClick={cancelEdit}>
            Cancelar
          </button>
        </>
      ) : (
        <>
          <button
            type="button"
            className={styles.btnSecondary}
            disabled={savingId === r.id || r.status === 'cancelado'}
            onClick={() => startEdit(r)}
          >
            Reagendar
          </button>
          <button
            type="button"
            className={styles.btnSecondary}
            disabled={savingId === r.id || r.status === 'realizado'}
            onClick={() => onStatus(r.id, 'realizado')}
          >
            Realizado
          </button>
          <button
            type="button"
            className={styles.btnDanger}
            disabled={savingId === r.id || r.status === 'cancelado'}
            onClick={() => onStatus(r.id, 'cancelado')}
          >
            Cancelar
          </button>
        </>
      )}
    </div>
  )

  return (
    <>
      <header className={styles.sectionHeader}>
        <h1 className={styles.sectionTitle}>Agendamentos</h1>
        <p className={styles.sectionSubtitle}>
          Todas as marcações feitas pelos clientes. Pesquise, filtre, crie novos agendamentos e exporte a
          lista actual.
        </p>
      </header>

      {error && (
        <div className={styles.erroSection}>
          <p className={styles.erro}>{error}</p>
        </div>
      )}

      <div className={styles.agToolbar}>
        <button type="button" className={styles.btnSalvar} onClick={() => setShowNovo((v) => !v)}>
          {showNovo ? 'Fechar formulário' : 'Novo agendamento'}
        </button>
        <button
          type="button"
          className={styles.btnSecondary}
          onClick={() => downloadCsv(displayRows, showServicoCol)}
          disabled={rows.length === 0}
        >
          Exportar CSV
        </button>
      </div>

      {showNovo && (
        <div className={styles.sectionCard}>
          <h2 className={styles.sectionListTitle} style={{ marginTop: 0, border: 'none', paddingBottom: 0 }}>
            Novo agendamento
          </h2>
          <form onSubmit={handleCreate}>
            <div className={styles.agFilters} style={{ marginBottom: 12 }}>
              <div className={styles.formGroup}>
                <label htmlFor="novo-user-q">Pesquisar cliente</label>
                <div className={styles.inlineSearchRow}>
                  <input
                    id="novo-user-q"
                    value={novoUsuarioQ}
                    onChange={(e) => setNovoUsuarioQ(e.target.value)}
                    placeholder="Nome, email ou telefone"
                  />
                  <button type="button" className={styles.btnSecondary} onClick={searchUsers} disabled={searchingUsers}>
                    {searchingUsers ? '…' : 'Pesquisar'}
                  </button>
                </div>
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="novo-user">Cliente</label>
                <select
                  id="novo-user"
                  value={novoUsuarioId ?? ''}
                  onChange={(e) => setNovoUsuarioId(Number(e.target.value) || null)}
                >
                  <option value="">— Seleccionar —</option>
                  {novoUsuarios.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.nome} ({u.email})
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="novo-prof">Profissional</label>
                <select id="novo-prof" value={novoProfId} onChange={(e) => setNovoProfId(e.target.value)} required>
                  <option value="">— Seleccionar —</option>
                  {profissionais.map((p) => (
                    <option key={p.id} value={String(p.id)}>
                      {p.nome}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="novo-unidade">Unidade</label>
                <select
                  id="novo-unidade"
                  value={novoUnidadeId}
                  onChange={(e) => setNovoUnidadeId(e.target.value)}
                  required
                >
                  {unidades.map((u) => (
                    <option key={u.id} value={String(u.id)}>
                      {u.nome}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="novo-servico">Serviço</label>
                <select id="novo-servico" value={novoServicoId} onChange={(e) => setNovoServicoId(e.target.value)}>
                  <option value="">— Opcional —</option>
                  {servicos.map((s) => (
                    <option key={s.id} value={String(s.id)}>
                      {s.nome}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="novo-data">Data</label>
                <input
                  id="novo-data"
                  type="date"
                  value={novoData}
                  onChange={(e) => setNovoData(e.target.value)}
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="novo-hora">Hora</label>
                <input
                  id="novo-hora"
                  type="time"
                  value={novoHora}
                  onChange={(e) => setNovoHora(e.target.value)}
                  required
                />
              </div>
            </div>
            <div className={styles.formActions}>
              <button type="submit" className={styles.btnSalvar} disabled={novoSaving}>
                {novoSaving ? 'A criar…' : 'Criar agendamento'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className={styles.agFilters}>
        <div className={styles.formGroup}>
          <label htmlFor="ag-f-q">Pesquisa</label>
          <input
            id="ag-f-q"
            type="search"
            className={styles.input}
            value={filtroQ}
            onChange={(e) => setFiltroQ(e.target.value)}
            placeholder="Cliente, email…"
          />
        </div>
        <div className={styles.formGroup}>
          <label htmlFor="ag-f-prof">Profissional</label>
          <select id="ag-f-prof" className={styles.input} value={filtroProf} onChange={(e) => setFiltroProf(e.target.value)}>
            <option value="">Todos</option>
            {profissionais.map((p) => (
              <option key={p.id} value={String(p.id)}>
                {p.nome}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.formGroup}>
          <label htmlFor="ag-f-status">Estado</label>
          <select id="ag-f-status" className={styles.input} value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)}>
            <option value="">Todos</option>
            <option value="agendado">Agendado</option>
            <option value="realizado">Realizado</option>
            <option value="cancelado">Cancelado</option>
          </select>
        </div>
        <div className={styles.formGroup}>
          <label htmlFor="ag-f-desde">Data desde</label>
          <input id="ag-f-desde" type="date" className={styles.input} value={filtroDesde} onChange={(e) => setFiltroDesde(e.target.value)} />
        </div>
        <div className={styles.formGroup}>
          <label htmlFor="ag-f-ate">Data até</label>
          <input id="ag-f-ate" type="date" className={styles.input} value={filtroAte} onChange={(e) => setFiltroAte(e.target.value)} />
        </div>
        <div className={styles.agFilterActions}>
          <button type="button" className={styles.btnSecondary} onClick={applyFilters} disabled={loading}>
            {loading ? 'A carregar…' : 'Filtrar'}
          </button>
        </div>
      </div>

      <div className={styles.agSummary}>
        <span>
          <strong>{total}</strong> total
        </span>
        <span>
          Página <strong>{page}</strong> de <strong>{pages || 1}</strong>
        </span>
      </div>

      <div className={styles.agTableCard}>
        {loading ? (
          <p className={styles.placeholderMsg}>A carregar agendamentos…</p>
        ) : rows.length === 0 ? (
          <p className={styles.listEmpty}>Nenhum agendamento encontrado com estes filtros.</p>
        ) : (
          <>
            <div className={styles.agDesktopTable}>
              <div className={styles.agTableScroll}>
                <table className={styles.agTable}>
                  <thead>
                    <tr>
                      <th>Data</th>
                      <th>Hora</th>
                      <th>Cliente</th>
                      <th>Contacto</th>
                      <th>Profissional</th>
                      <th>Unidade</th>
                      {showServicoCol && <th>Serviço</th>}
                      <th>Estado</th>
                      <th>Acções</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayRows.map((r) => {
                      const isEditing = editingId === r.id
                      return (
                        <tr key={r.id}>
                          <td>
                            {isEditing ? (
                              <input
                                type="date"
                                value={editForm.data}
                                onChange={(e) => setEditForm((f) => ({ ...f, data: e.target.value }))}
                                className={styles.inputCompact}
                              />
                            ) : (
                              formatDataPt(r.data)
                            )}
                          </td>
                          <td>
                            {isEditing ? (
                              <input
                                type="time"
                                value={editForm.hora}
                                onChange={(e) => setEditForm((f) => ({ ...f, hora: e.target.value }))}
                                className={styles.inputCompact}
                              />
                            ) : (
                              r.hora
                            )}
                          </td>
                          <td>
                            <div className={styles.agClienteCell}>
                              {(() => {
                                const id = resolveClienteIdentificador(r)
                                return (
                                  <>
                                    <strong>{id.primary}</strong>
                                    {id.secondary && (
                                      <span className={styles.agClienteSub}>{id.secondary}</span>
                                    )}
                                  </>
                                )
                              })()}
                            </div>
                          </td>
                          <td className={styles.agContactCell}>
                            {r.cliente_email && <div>{r.cliente_email}</div>}
                            {r.cliente_telefone && <div>{r.cliente_telefone}</div>}
                            {!r.cliente_email && !r.cliente_telefone && '—'}
                          </td>
                          <td>
                            {isEditing ? (
                              <select
                                value={editForm.profissional_id}
                                onChange={(e) => setEditForm((f) => ({ ...f, profissional_id: e.target.value }))}
                                className={styles.inputCompact}
                              >
                                {profissionais.map((p) => (
                                  <option key={p.id} value={String(p.id)}>
                                    {p.nome}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              r.profissional
                            )}
                          </td>
                          <td>
                            {isEditing ? (
                              <select
                                value={editForm.unidade_id}
                                onChange={(e) => setEditForm((f) => ({ ...f, unidade_id: e.target.value }))}
                                className={styles.inputCompact}
                              >
                                {unidades.map((u) => (
                                  <option key={u.id} value={String(u.id)}>
                                    {u.nome}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              r.unidade
                            )}
                          </td>
                          {showServicoCol && <td>{r.servico_nome || '—'}</td>}
                          <td>
                            <span className={statusClass(r.status)}>{statusLabel(r.status)}</span>
                          </td>
                          <td>{renderRowActions(r, isEditing)}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className={styles.agMobileCards}>
              {displayRows.map((r) => {
                const isEditing = editingId === r.id
                return (
                  <article key={r.id} className={styles.agMobileCard}>
                    <div className={styles.agMobileCardHead}>
                      {(() => {
                        const id = resolveClienteIdentificador(r)
                        return (
                          <div className={styles.agMobileCardTitleBlock}>
                            <p className={styles.agMobileCardTitle}>{id.primary}</p>
                            {id.secondary && (
                              <p className={styles.agMobileCardMeta}>{id.secondary}</p>
                            )}
                          </div>
                        )
                      })()}
                      <div className={styles.agMobileCardWhen}>
                        <div>{formatDataPt(r.data)}</div>
                        <div>{r.hora}</div>
                      </div>
                    </div>
                    <p className={styles.agMobileCardMeta}>
                      <span className={statusClass(r.status)}>{statusLabel(r.status)}</span>
                    </p>
                    {isEditing ? (
                      <div className={styles.agMobileCardEdit}>
                        <input
                          type="date"
                          value={editForm.data}
                          onChange={(e) => setEditForm((f) => ({ ...f, data: e.target.value }))}
                          className={styles.inputCompact}
                        />
                        <input
                          type="time"
                          value={editForm.hora}
                          onChange={(e) => setEditForm((f) => ({ ...f, hora: e.target.value }))}
                          className={styles.inputCompact}
                        />
                        <select
                          value={editForm.profissional_id}
                          onChange={(e) => setEditForm((f) => ({ ...f, profissional_id: e.target.value }))}
                          className={styles.inputCompact}
                        >
                          {profissionais.map((p) => (
                            <option key={p.id} value={String(p.id)}>
                              {p.nome}
                            </option>
                          ))}
                        </select>
                        <select
                          value={editForm.unidade_id}
                          onChange={(e) => setEditForm((f) => ({ ...f, unidade_id: e.target.value }))}
                          className={styles.inputCompact}
                        >
                          {unidades.map((u) => (
                            <option key={u.id} value={String(u.id)}>
                              {u.nome}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <>
                        <p className={styles.agMobileCardMeta}>
                          {r.profissional} · {r.unidade}
                        </p>
                        {showServicoCol && r.servico_nome && (
                          <p className={styles.agMobileCardMeta}>{r.servico_nome}</p>
                        )}
                      </>
                    )}
                    {renderRowActions(r, isEditing)}
                  </article>
                )
              })}
            </div>
          </>
        )}
      </div>

      {pages > 1 && (
        <div className={styles.agPagination}>
          <button
            type="button"
            className={styles.btnSecondary}
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Anterior
          </button>
          <span className={styles.agPaginationInfo}>
            Página {page} de {pages}
          </span>
          <button
            type="button"
            className={styles.btnSecondary}
            disabled={page >= pages || loading}
            onClick={() => setPage((p) => p + 1)}
          >
            Seguinte
          </button>
        </div>
      )}
    </>
  )
}
